import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { getProducts } from "@/lib/api";
import { PRODUCT_CATEGORIES, categoryLabel } from "@/constants/categories";
import { ShoppingBag } from "@phosphor-icons/react";

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?crop=entropy&cs=srgb&fm=jpg&q=80&w=600";

export const ProductsSection = () => {
  const { t, lang } = useLang();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  if (products.length === 0) return null;

  const name = (p) => (lang === "gr" ? p.name_gr : p.name_en);
  const desc = (p) => (lang === "gr" ? p.description_gr : p.description_en);

  const order = PRODUCT_CATEGORIES.map((c) => c.key);
  const grouped = {};
  for (const p of products) {
    const key = order.includes(p.category) ? p.category : "other";
    (grouped[key] = grouped[key] || []).push(p);
  }
  const activeCats = order.filter((k) => grouped[k]?.length);

  return (
    <section id="products" className="mx-auto max-w-6xl px-5 py-10">
      <div className="mb-10 flex items-center gap-3">
        <ShoppingBag size={26} weight="fill" className="text-[#24483A]" />
        <div>
          <h2 className="font-serif text-2xl tracking-tight sm:text-3xl">{t.products.title}</h2>
          <p className="text-sm text-[#8F8A79]">{t.products.subtitle}</p>
        </div>
      </div>

      {activeCats.map((cat) => (
        <div key={cat} data-testid={`category-group-${cat}`} className="mb-12">
          <div className="mb-5 flex items-center gap-4">
            <h3 className="font-serif text-xl tracking-tight text-[#20261E] sm:text-2xl">
              {categoryLabel(cat, lang)}
            </h3>
            <div className="h-px flex-1 bg-[#20261E]/10" />
            <span className="text-xs text-[#8F8A79]">{grouped[cat].length}</span>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {grouped[cat].map((p, i) => (
              <motion.div
                key={p.id}
                data-testid={`product-${p.id}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group overflow-hidden rounded-2xl border border-[#20261E]/10 bg-white shadow-[0_8px_32px_rgba(28,37,29,0.06)]"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#E7E1D2]">
                  <img
                    src={p.image_url || PLACEHOLDER}
                    alt={name(p)}
                    onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h4 className="font-serif text-xl tracking-tight text-[#20261E]">{name(p)}</h4>
                  {desc(p) && <p className="mt-2 text-sm leading-relaxed text-[#5B6053]">{desc(p)}</p>}
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-serif text-2xl font-bold text-[#24483A]">
                      €{Number(p.price).toFixed(2).replace(/\.00$/, "")}
                    </span>
                    <span className="text-xs text-[#8F8A79]">{t.products.atPharmacy}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
};
